import { Chess, type Square as CjSquare } from 'chess.js';
import type { Color, Square } from '@domain/chess/types';

export type RelationKind = 'attack' | 'protect';

export type FocusRelationKind = 'enemy-attacked' | 'own-defends';

export interface PieceRelation {
  from: Square;
  to: Square;
  kind: RelationKind;
}

export interface FocusRelation {
  from: Square;
  to: Square;
  kind: FocusRelationKind;
}

/**
 * Occupied-to-occupied attacks (enemy) and protections (friendly).
 */
export function pieceRelations(fen: string): PieceRelation[] {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    return [];
  }

  const out: PieceRelation[] = [];
  for (const row of game.board()) {
    for (const cell of row) {
      if (!cell) continue;
      const to = cell.square as Square;
      const owner = cell.color as Color;
      const opp: Color = owner === 'w' ? 'b' : 'w';
      for (const from of game.attackers(to as CjSquare, opp)) {
        out.push({ from: from as Square, to, kind: 'attack' });
      }
      for (const from of game.attackers(to as CjSquare, owner)) {
        if (from === to) continue;
        out.push({ from: from as Square, to, kind: 'protect' });
      }
    }
  }
  return out;
}

function focusKind(rel: PieceRelation, start: Square): FocusRelationKind | null {
  if (rel.from !== start || rel.to === start) return null;
  return rel.kind === 'attack' ? 'enemy-attacked' : 'own-defends';
}

/**
 * Outgoing lines from the focused piece (selected or last-moved).
 * Attacks an enemy = red; attacks/guards own = blue.
 */
export function relationsFromPiece(
  relations: readonly PieceRelation[],
  start: Square | null,
): FocusRelation[] {
  if (!start) return [];
  const seen = new Set<string>();
  const out: FocusRelation[] = [];
  for (const rel of relations) {
    const kind = focusKind(rel, start);
    if (!kind) continue;
    const key = `${kind}:${rel.from}:${rel.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ from: rel.from, to: rel.to, kind });
  }
  return out;
}
